/**
 * Snowflake-IDs (64 Bit, zeitlich sortierbar) – siehe DECISIONS D11.
 *  [ 42 Bit Millisekunden seit LF_EPOCH | 10 Bit Worker | 12 Bit Sequenz ]
 * Worker 0 = PostgreSQL-Funktion `lf_snowflake()` (Standard für alle Tabellen),
 * Worker 1..1023 = Anwendungsprozesse (z. B. Brücke beim Import historischer Nachrichten).
 * Über die Leitung immer als Dezimal-String.
 */
export const LF_EPOCH = 1767225600000n; // 2026-01-01T00:00:00Z
export const LF_EPOCH_MS = Number(LF_EPOCH);

const WORKER_BITS = 10n;
const SEQ_BITS = 12n;
const MAX_SEQ = (1n << SEQ_BITS) - 1n;
const MAX_WORKER = (1n << WORKER_BITS) - 1n;

export type Snowflake = string;

export class SnowflakeGenerator {
  private lastMs = -1n;
  private seq = 0n;
  private readonly worker: bigint;

  constructor(workerId: number) {
    const w = BigInt(workerId);
    if (w < 1n || w > MAX_WORKER) throw new Error(`workerId muss 1..${MAX_WORKER} sein (0 ist der Datenbank vorbehalten)`);
    this.worker = w;
  }

  /** Neue ID für „jetzt". Monoton steigend innerhalb des Prozesses. */
  next(): bigint {
    let now = BigInt(Date.now());
    if (now < this.lastMs) now = this.lastMs; // Uhr läuft rückwärts → weiter auf letzter ms
    if (now === this.lastMs) {
      this.seq = (this.seq + 1n) & MAX_SEQ;
      if (this.seq === 0n) now = this.lastMs + 1n;
    } else {
      this.seq = 0n;
    }
    this.lastMs = now;
    return ((now - LF_EPOCH) << (WORKER_BITS + SEQ_BITS)) | (this.worker << SEQ_BITS) | this.seq;
  }

  /**
   * ID für einen historischen Zeitpunkt (Import). Sortiert korrekt nach Zeit; `seq` muss innerhalb
   * derselben Millisekunde eindeutig sein (z. B. laufender Zähler des Imports).
   */
  at(timestampMs: number, seq = 0): bigint {
    return snowflakeFromTimestamp(timestampMs, Number(this.worker), seq);
  }
}

export function snowflakeFromTimestamp(timestampMs: number, workerId = 0, seq = 0): bigint {
  const ms = BigInt(Math.floor(timestampMs)) - LF_EPOCH;
  // Zeitpunkte vor der Epoche (alter Discord-Verlauf) werden auf 0 geklemmt – Sortierung bleibt per seq stabil.
  const t = ms < 0n ? 0n : ms;
  return (t << (WORKER_BITS + SEQ_BITS)) | ((BigInt(workerId) & MAX_WORKER) << SEQ_BITS) | (BigInt(seq) & MAX_SEQ);
}

/** Kleinste ID für einen Zeitpunkt – für Filter `before:`/`after:` in Suche und Paginierung. */
export function snowflakeLowerBound(timestampMs: number): bigint {
  return snowflakeFromTimestamp(timestampMs, 0, 0);
}

export function timestampFromSnowflake(id: bigint | string): number {
  const v = typeof id === 'bigint' ? id : BigInt(id);
  return Number((v >> (WORKER_BITS + SEQ_BITS)) + LF_EPOCH);
}

export function isSnowflake(v: unknown): v is Snowflake {
  return typeof v === 'string' && /^\d{1,20}$/.test(v) && BigInt(v) <= 0x7fffffffffffffffn;
}

/** Vergleich zweier Snowflake-Strings (numerisch, nicht lexikografisch!). */
export function compareSnowflakes(a: Snowflake, b: Snowflake): number {
  const x = BigInt(a);
  const y = BigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
}
